import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Switch, TextField, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { Star, Settings, People, TrendingUp } from '@mui/icons-material';

const LoyaltyRewards: React.FC = () => {
  const [settings, setSettings] = useState({
    enabled: true,
    pointsPerDollar: 1,
    rewardThreshold: 200,
    rewardValue: 50,
    membershipTiers: {
      basic: { minSpent: 0, discount: 10 },
      premium: { minSpent: 200, discount: 15 },
      vip: { minSpent: 500, discount: 20 }
    }
  });
  const [clients, setClients] = useState<any[]>([]);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  useEffect(() => {
    fetchLoyaltySettings();
    fetchClientsWithLoyalty();
  }, []);

  const fetchLoyaltySettings = async () => {
    try {
      const response = await fetch('https://salon.rockyroadsolutions.com/api/loyalty/settings');
      if (response.ok) {
        const data = await response.json();
        setSettings({
          enabled: data.enabled,
          pointsPerDollar: data.points_per_dollar,
          rewardThreshold: data.reward_threshold,
          rewardValue: data.reward_value,
          membershipTiers: {
            basic: { minSpent: 0, discount: data.basic_discount },
            premium: { minSpent: data.premium_min_spent, discount: data.premium_discount },
            vip: { minSpent: data.vip_min_spent, discount: data.vip_discount }
          }
        });
      }
    } catch (error) {
      console.error('Error loading loyalty settings:', error);
    }
  };

  const fetchClientsWithLoyalty = async () => {
    try {
      const [appointmentsResponse, clientsResponse] = await Promise.all([
        fetch('https://salon.rockyroadsolutions.com/api/appointments'),
        fetch('https://salon.rockyroadsolutions.com/api/clients')
      ]);

      if (appointmentsResponse.ok && clientsResponse.ok) {
        const appointments = await appointmentsResponse.json();
        const allClients = await clientsResponse.json();

        const clientStats = allClients.map((client: any) => {
          const clientAppointments = appointments.filter((apt: any) => 
            apt.client?.id === client.id && apt.status.toUpperCase() === 'COMPLETED'
          );

          const totalSpent = clientAppointments.reduce((sum: number, apt: any) => 
            sum + parseFloat(apt.totalPrice) + parseFloat(apt.tipAmount || 0), 0
          );

          const loyaltyPoints = Math.floor(totalSpent * settings.pointsPerDollar);
          
          let tier = 'Basic';
          if (totalSpent >= settings.membershipTiers.vip.minSpent) tier = 'VIP';
          else if (totalSpent >= settings.membershipTiers.premium.minSpent) tier = 'Premium';

          return {
            ...client,
            totalSpent,
            loyaltyPoints,
            tier,
            totalAppointments: clientAppointments.length,
            lastVisit: clientAppointments.length > 0 ? 
              new Date(Math.max(...clientAppointments.map((apt: any) => new Date(apt.appointmentDate).getTime()))) : null
          };
        });

        setClients(clientStats.sort((a: any, b: any) => b.loyaltyPoints - a.loyaltyPoints));
      }
    } catch (error) {
      console.error('Error fetching client loyalty data:', error);
    }
  };

  const handleSettingsUpdate = async () => {
    try {
      const response = await fetch('https://salon.rockyroadsolutions.com/api/loyalty/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          enabled: settings.enabled,
          points_per_dollar: settings.pointsPerDollar,
          reward_threshold: settings.rewardThreshold,
          reward_value: settings.rewardValue,
          basic_discount: settings.membershipTiers.basic.discount,
          premium_min_spent: settings.membershipTiers.premium.minSpent,
          premium_discount: settings.membershipTiers.premium.discount,
          vip_min_spent: settings.membershipTiers.vip.minSpent,
          vip_discount: settings.membershipTiers.vip.discount
        })
      });
      
      if (response.ok) {
        console.log('Loyalty settings saved to database');
        fetchClientsWithLoyalty(); // Recalculate with new settings
        setIsSettingsOpen(false);
      }
    } catch (error) {
      console.error('Error saving loyalty settings:', error);
    }
  };

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'VIP': return '#E91E63';
      case 'Premium': return '#9C27B0';
      default: return '#FF9800';
    }
  };

  const totalActiveClients = clients.length;
  const totalPointsIssued = clients.reduce((sum, client) => sum + client.loyaltyPoints, 0);
  const averageSpending = clients.length > 0 ? clients.reduce((sum, client) => sum + client.totalSpent, 0) / clients.length : 0;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Loyalty Rewards Management</Typography>
        <Button variant="outlined" startIcon={<Settings />} onClick={() => setIsSettingsOpen(true)}>
          Settings
        </Button>
      </Box>

      {/* Overview Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <People sx={{ fontSize: 40, color: '#E91E63', mb: 1 }} />
              <Typography variant="h4" fontWeight={600}>{totalActiveClients}</Typography>
              <Typography variant="body2" color="text.secondary">Active Members</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Star sx={{ fontSize: 40, color: '#FFD700', mb: 1 }} />
              <Typography variant="h4" fontWeight={600}>{totalPointsIssued.toLocaleString()}</Typography>
              <Typography variant="body2" color="text.secondary">Total Points Issued</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <TrendingUp sx={{ fontSize: 40, color: '#4CAF50', mb: 1 }} />
              <Typography variant="h4" fontWeight={600}>${averageSpending.toFixed(0)}</Typography>
              <Typography variant="body2" color="text.secondary">Avg. Customer Spending</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                <Typography variant="body2" color="text.secondary" mr={1}>Status:</Typography>
                <Chip 
                  label={settings.enabled ? 'Active' : 'Disabled'} 
                  color={settings.enabled ? 'success' : 'error'}
                  size="small"
                />
              </Box>
              <Typography variant="body2" color="text.secondary">
                {settings.pointsPerDollar} point per $1 spent
              </Typography>
              <Typography variant="body2" color="text.secondary">
                ${settings.rewardValue} reward at {settings.rewardThreshold} points
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Client Loyalty Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Client Loyalty Status</Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Client</TableCell>
                  <TableCell align="center">Tier</TableCell>
                  <TableCell align="center">Points</TableCell>
                  <TableCell align="center">Total Spent</TableCell>
                  <TableCell align="center">Appointments</TableCell>
                  <TableCell align="center">Last Visit</TableCell>
                  <TableCell align="center">Discount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {clients.map((client) => (
                  <TableRow key={client.id}>
                    <TableCell>
                      <Box>
                        <Typography variant="body1" fontWeight={500}>
                          {client.first_name || client.firstName} {client.last_name || client.lastName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {client.email}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={client.tier} 
                        sx={{ 
                          backgroundColor: getTierColor(client.tier), 
                          color: 'white',
                          fontWeight: 600
                        }}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                        <Star sx={{ fontSize: 16, color: '#FFD700' }} />
                        <Typography variant="body1" fontWeight={600}>
                          {client.loyaltyPoints}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body1" fontWeight={600} color="primary">
                        ${client.totalSpent.toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">{client.totalAppointments}</TableCell>
                    <TableCell align="center">
                      {client.lastVisit ? client.lastVisit.toLocaleDateString() : 'Never'}
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body1" fontWeight={600} color="success.main">
                        {settings.membershipTiers[client.tier.toLowerCase() as keyof typeof settings.membershipTiers]?.discount || 0}%
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Settings Dialog */}
      <Dialog open={isSettingsOpen} onClose={() => setIsSettingsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Loyalty Rewards Settings</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" gap={2}>
                <Typography variant="body1">Enable Loyalty Program:</Typography>
                <Switch 
                  checked={settings.enabled} 
                  onChange={(e) => setSettings({...settings, enabled: e.target.checked})}
                />
              </Box>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Points per Dollar Spent"
                type="number"
                value={settings.pointsPerDollar}
                onChange={(e) => setSettings({...settings, pointsPerDollar: parseFloat(e.target.value)})}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Reward Threshold (Points)"
                type="number"
                value={settings.rewardThreshold}
                onChange={(e) => setSettings({...settings, rewardThreshold: parseInt(e.target.value)})}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Reward Value ($)"
                type="number"
                value={settings.rewardValue}
                onChange={(e) => setSettings({...settings, rewardValue: parseFloat(e.target.value)})}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>Membership Tiers</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="subtitle2" gutterBottom>Basic Tier</Typography>
              <TextField
                fullWidth
                label="Discount %"
                type="number"
                value={settings.membershipTiers.basic.discount}
                onChange={(e) => setSettings({
                  ...settings, 
                  membershipTiers: {
                    ...settings.membershipTiers,
                    basic: {...settings.membershipTiers.basic, discount: parseInt(e.target.value)}
                  }
                })}
                sx={{ mb: 1 }}
              />
            </Grid>
            <Grid item xs={4}>
              <Typography variant="subtitle2" gutterBottom>Premium Tier</Typography>
              <TextField
                fullWidth
                label="Min Spent ($)"
                type="number"
                value={settings.membershipTiers.premium.minSpent}
                onChange={(e) => setSettings({
                  ...settings, 
                  membershipTiers: {
                    ...settings.membershipTiers,
                    premium: {...settings.membershipTiers.premium, minSpent: parseInt(e.target.value)}
                  }
                })}
                sx={{ mb: 1 }}
              />
              <TextField
                fullWidth
                label="Discount %"
                type="number"
                value={settings.membershipTiers.premium.discount}
                onChange={(e) => setSettings({
                  ...settings, 
                  membershipTiers: {
                    ...settings.membershipTiers,
                    premium: {...settings.membershipTiers.premium, discount: parseInt(e.target.value)}
                  }
                })}
              />
            </Grid>
            <Grid item xs={4}>
              <Typography variant="subtitle2" gutterBottom>VIP Tier</Typography>
              <TextField
                fullWidth
                label="Min Spent ($)"
                type="number"
                value={settings.membershipTiers.vip.minSpent}
                onChange={(e) => setSettings({
                  ...settings, 
                  membershipTiers: {
                    ...settings.membershipTiers,
                    vip: {...settings.membershipTiers.vip, minSpent: parseInt(e.target.value)}
                  }
                })}
                sx={{ mb: 1 }}
              />
              <TextField
                fullWidth
                label="Discount %"
                type="number"
                value={settings.membershipTiers.vip.discount}
                onChange={(e) => setSettings({
                  ...settings, 
                  membershipTiers: {
                    ...settings.membershipTiers,
                    vip: {...settings.membershipTiers.vip, discount: parseInt(e.target.value)}
                  }
                })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsSettingsOpen(false)}>Cancel</Button>
          <Button onClick={handleSettingsUpdate} variant="contained">Save Settings</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LoyaltyRewards;
